import { useCallback, useMemo } from 'react';
import type { ModelId, ViewId } from 'balus-common';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import type { StickyModelContentsOperation } from '@view-model/adapter';
import type { ModelCollection } from '@view-model/domain/model/ModelCollection';
import { useViewModelOperationLogSender } from '@model-framework/action-log';
import type { ViewCollectionContents } from '@view-model/domain/view/ViewCollectionContents';
import { Point } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import { useGetTransformedVisibleAreaCenterPoint } from '../VisibleAreaCenterPointProvider';
import { useClientToCanvasPoint } from '../useClientToCanvasPoint';
import type { ThemeColor } from '@view-model/models/common/color';
import { CommentConstants } from '@view-model/models/sticky/ModelComment/constants';
import {
  COMMENT_GHOST_DRAG_HANDLE_HEIGHT,
  COMMENT_GHOST_FORM_CONTENT_HEIGHT,
} from '@user/pages/ViewModelPage/FloatingUIContainer/ViewModelToolbar/CommentDragGhost';

const COMMENT_GHOST_HEIGHT = COMMENT_GHOST_DRAG_HANDLE_HEIGHT + COMMENT_GHOST_FORM_CONTENT_HEIGHT;

const getOperationForView = (
  view: ViewEntity,
  models: ModelCollection,
  operations: Record<ModelId, StickyModelContentsOperation>
): StickyModelContentsOperation | undefined => {
  const model = models.get(view.modelKey);
  if (!model) return undefined;
  return operations[model.id];
};

const defaultValue = { selectedView: undefined, selectedViewRect: Rect.fromSize(0, 0) };

const useSelectedViewContent = (
  selectedViewIds: Set<ViewId>,
  views: ViewCollectionContents
): { selectedView: ViewEntity | undefined; selectedViewRect: Rect } => {
  return useMemo(() => {
    if (selectedViewIds.size !== 1) return defaultValue;
    const selectedViewId = Array.from(selectedViewIds)[0];

    const selectedView = views.findView(selectedViewId);
    const selectedViewPosition = views.findPosition(selectedViewId);

    if (!selectedView || !selectedViewPosition) return defaultValue;

    return {
      selectedView,
      selectedViewRect: selectedView.getRect(selectedViewPosition),
    };
  }, [selectedViewIds, views]);
};

const useSelectedStickyModelContentsOperation = (
  operations: Record<ModelId, StickyModelContentsOperation>,
  models: ModelCollection,
  selectedView: ViewEntity | undefined
): StickyModelContentsOperation | undefined => {
  if (!selectedView) return;

  const selectedModel = models.get(selectedView.modelKey);
  if (!selectedModel) return;

  return operations[selectedModel.id];
};

export const useViewMenuHandlers = (
  readonly: boolean,
  selectedViewIds: Set<ViewId>,
  views: ViewCollectionContents,
  models: ModelCollection,
  operations: Record<ModelId, StickyModelContentsOperation>,
  handleCreatingCommentAdd: (modelId: ModelId, position: Point) => void,
  onSelectView: (viewId: ViewId) => void
): {
  handleAddZone(): void;
  handleAddDescriptionPanel(): void;
  handleAddDescriptionPanelByDrag(clientPoint: { x: number; y: number }): void;
  handleAddComment(): void;
  handleAddCommentByDrag(clientPoint: { x: number; y: number }): void;
  handleAddNodeWithColor(color: ThemeColor): void;
  handleAddNodeByDragWithColor(clientPoint: { x: number; y: number }, color: ThemeColor): void;
  handleAddZoneWithColor(color: ThemeColor): void;
  handleAddZoneByDragWithColor(clientPoint: { x: number; y: number }, color: ThemeColor): void;
} => {
  const logSender = useViewModelOperationLogSender();
  const { selectedView, selectedViewRect } = useSelectedViewContent(selectedViewIds, views);
  const getTransformedVisibleAreaCenterPoint = useGetTransformedVisibleAreaCenterPoint();
  const selectedOperation = useSelectedStickyModelContentsOperation(operations, models, selectedView);
  const clientToCanvasPoint = useClientToCanvasPoint();

  const handleAddZone = useCallback((): void => {
    if (readonly || !selectedView || !selectedOperation) return;
    const transformedVisibleAreaCenterPoint = getTransformedVisibleAreaCenterPoint(selectedViewRect);
    const createPoint = selectedOperation.getCreatePoint(transformedVisibleAreaCenterPoint);
    const zoneId = selectedOperation.createZone(createPoint);

    logSender('sticky:zone:create', {
      zoneId,
      cause: 'menu_button',
      viewId: selectedView.id,
      viewName: selectedView.name.value,
    }).then();
  }, [logSender, readonly, selectedOperation, selectedView, selectedViewRect, getTransformedVisibleAreaCenterPoint]);

  const handleAddDescriptionPanel = useCallback(() => {
    if (readonly || !selectedView || !selectedOperation) return;
    const transformedVisibleAreaCenterPoint = getTransformedVisibleAreaCenterPoint(selectedViewRect);
    const createPoint = selectedOperation.getCreatePoint(transformedVisibleAreaCenterPoint);
    const panelId = selectedOperation.addDescriptionPanel(createPoint);

    logSender('sticky:description_panel:create', {
      panelId,
      cause: 'menu_button',
      viewId: selectedView.id,
      viewName: selectedView.name.value,
    }).then();
  }, [logSender, readonly, selectedOperation, selectedView, selectedViewRect, getTransformedVisibleAreaCenterPoint]);

  // 説明パネルをドラッグ&ドロップで指定した位置に作成する共通ロジック
  const createDescriptionPanelByDrag = useCallback(
    (clientPoint: { x: number; y: number }): void => {
      if (readonly) return;
      const canvasPoint = clientToCanvasPoint(new Point(clientPoint.x, clientPoint.y));
      if (!canvasPoint) return;

      const targetView = views.findForegroundViewByPosition(canvasPoint) ?? selectedView;
      if (!targetView) return;

      const targetOperation = getOperationForView(targetView, models, operations);
      if (!targetOperation) return;

      if (!selectedViewIds.has(targetView.id)) {
        onSelectView(targetView.id);
      }

      const targetViewRect = views.getRectOf(targetView.id);
      if (!targetViewRect) return;
      const { x: viewTopLeftX, y: viewTopLeftY } = targetViewRect.topLeft();
      const viewLocalPoint = canvasPoint.addXY(-viewTopLeftX, -viewTopLeftY);

      const panelId = targetOperation.addDescriptionPanelAtClickedPosition(viewLocalPoint);

      logSender('sticky:description_panel:create', {
        panelId,
        cause: 'toolbar_drag',
        viewId: targetView.id,
        viewName: targetView.name.value,
      });
    },
    [clientToCanvasPoint, logSender, models, onSelectView, operations, readonly, selectedView, selectedViewIds, views]
  );

  const handleAddDescriptionPanelByDrag = useCallback(
    (clientPoint: { x: number; y: number }): void => {
      createDescriptionPanelByDrag(clientPoint);
    },
    [createDescriptionPanelByDrag]
  );

  const handleAddComment = useCallback(() => {
    if (readonly || !selectedView || !selectedOperation) return;
    const transformedVisibleAreaCenterPoint = getTransformedVisibleAreaCenterPoint(selectedViewRect);
    const createPoint = selectedOperation.getCreatePoint(transformedVisibleAreaCenterPoint);
    handleCreatingCommentAdd(selectedView.modelId, createPoint);
  }, [
    handleCreatingCommentAdd,
    readonly,
    selectedOperation,
    selectedView,
    selectedViewRect,
    getTransformedVisibleAreaCenterPoint,
  ]);

  // コメントをドラッグ&ドロップで指定した位置に作成する共通ロジック
  const createCommentByDrag = useCallback(
    (clientPoint: { x: number; y: number }): void => {
      if (readonly) return;
      const canvasPoint = clientToCanvasPoint(new Point(clientPoint.x, clientPoint.y));
      if (!canvasPoint) return;

      const targetView = views.findForegroundViewByPosition(canvasPoint) ?? selectedView;
      if (!targetView) return;

      if (!selectedViewIds.has(targetView.id)) {
        onSelectView(targetView.id);
      }

      const targetViewRect = views.getRectOf(targetView.id);
      if (!targetViewRect) return;
      const { x: viewTopLeftX, y: viewTopLeftY } = targetViewRect.topLeft();
      const viewLocalPoint = canvasPoint.addXY(-viewTopLeftX, -viewTopLeftY);

      // ドラッグゴーストは translate(-50%, -50%) でカーソル中心に描画されるため、
      // フォームの左上がゴーストの左上と一致するようにオフセットする
      const offsetPoint = viewLocalPoint.addXY(-CommentConstants.UISetting.width / 2, -COMMENT_GHOST_HEIGHT / 2);

      handleCreatingCommentAdd(targetView.modelId, offsetPoint);
    },
    [clientToCanvasPoint, handleCreatingCommentAdd, onSelectView, readonly, selectedView, selectedViewIds, views]
  );

  const handleAddCommentByDrag = useCallback(
    (clientPoint: { x: number; y: number }): void => {
      createCommentByDrag(clientPoint);
    },
    [createCommentByDrag]
  );

  // handleAddNodeByDrag / handleAddNodeByDragWithColor の共通ロジック
  const createNodeByDrag = useCallback(
    (clientPoint: { x: number; y: number }, color?: ThemeColor): void => {
      if (readonly) return;
      // クライアント座標 → キャンバス座標
      // SVG 未マウント時などは null が返るのでノード生成をスキップ
      const canvasPoint = clientToCanvasPoint(new Point(clientPoint.x, clientPoint.y));
      if (!canvasPoint) return;

      // ドロップ位置のビューを特定する。見つからなければ選択中ビューにフォールバック
      const targetView = views.findForegroundViewByPosition(canvasPoint) ?? selectedView;
      if (!targetView) return;

      const targetOperation = getOperationForView(targetView, models, operations);
      if (!targetOperation) return;

      // ドロップ先が現在選択中と異なるビューの場合は選択を切り替える
      if (!selectedViewIds.has(targetView.id)) {
        onSelectView(targetView.id);
      }

      // キャンバス座標 → ビュー内ローカル座標
      const targetViewRect = views.getRectOf(targetView.id);
      if (!targetViewRect) return;
      const { x: viewTopLeftX, y: viewTopLeftY } = targetViewRect.topLeft();
      const viewLocalPoint = canvasPoint.addXY(-viewTopLeftX, -viewTopLeftY);

      // ドロップ位置にゾーンがあればゾーン内に、なければビューに直接配置する
      const zoneKey = targetOperation.findZoneAtPoint(viewLocalPoint);
      const nodeId =
        color !== undefined
          ? zoneKey
            ? targetOperation.createNodeToZoneWithColor(viewLocalPoint, zoneKey, color)
            : targetOperation.createNodeAtClickedPositionWithColor(viewLocalPoint, color)
          : zoneKey
            ? targetOperation.createNodeToZone(viewLocalPoint, zoneKey)
            : targetOperation.createNodeAtClickedPosition(viewLocalPoint);

      logSender('sticky:node:create', {
        nodeId,
        cause: 'toolbar_drag',
        viewId: targetView.id,
        viewName: targetView.name.value,
        ...(zoneKey ? { zoneId: zoneKey.id } : {}),
      });
    },
    [clientToCanvasPoint, logSender, models, onSelectView, operations, readonly, selectedView, selectedViewIds, views]
  );

  const handleAddNodeWithColor = useCallback(
    (color: ThemeColor): void => {
      if (readonly || !selectedView || !selectedOperation) return;
      const transformedVisibleAreaCenterPoint = getTransformedVisibleAreaCenterPoint(selectedViewRect);
      const createPoint = selectedOperation.getCreatePoint(transformedVisibleAreaCenterPoint);
      const nodeId = selectedOperation.createNodeWithColor(createPoint, color);

      logSender('sticky:node:create', {
        nodeId,
        cause: 'menu_button',
        viewId: selectedView.id,
        viewName: selectedView.name.value,
      });
    },
    [logSender, readonly, selectedOperation, selectedView, selectedViewRect, getTransformedVisibleAreaCenterPoint]
  );

  const handleAddNodeByDragWithColor = useCallback(
    (clientPoint: { x: number; y: number }, color: ThemeColor): void => {
      createNodeByDrag(clientPoint, color);
    },
    [createNodeByDrag]
  );

  const handleAddZoneWithColor = useCallback(
    (color: ThemeColor): void => {
      if (readonly || !selectedView || !selectedOperation) return;
      const transformedVisibleAreaCenterPoint = getTransformedVisibleAreaCenterPoint(selectedViewRect);
      const createPoint = selectedOperation.getCreatePoint(transformedVisibleAreaCenterPoint);
      const zoneId = selectedOperation.createZoneWithColor(createPoint, color);

      logSender('sticky:zone:create', {
        zoneId,
        cause: 'menu_button',
        viewId: selectedView.id,
        viewName: selectedView.name.value,
      }).then();
    },
    [logSender, readonly, selectedOperation, selectedView, selectedViewRect, getTransformedVisibleAreaCenterPoint]
  );

  // ゾーンをドラッグ&ドロップで指定した位置に作成する共通ロジック
  const createZoneByDrag = useCallback(
    (clientPoint: { x: number; y: number }, color: ThemeColor): void => {
      if (readonly) return;
      const canvasPoint = clientToCanvasPoint(new Point(clientPoint.x, clientPoint.y));
      if (!canvasPoint) return;

      const targetView = views.findForegroundViewByPosition(canvasPoint) ?? selectedView;
      if (!targetView) return;

      const targetOperation = getOperationForView(targetView, models, operations);
      if (!targetOperation) return;

      if (!selectedViewIds.has(targetView.id)) {
        onSelectView(targetView.id);
      }

      const targetViewRect = views.getRectOf(targetView.id);
      if (!targetViewRect) return;
      const { x: viewTopLeftX, y: viewTopLeftY } = targetViewRect.topLeft();
      const viewLocalPoint = canvasPoint.addXY(-viewTopLeftX, -viewTopLeftY);

      const zoneId = targetOperation.createZoneAtClickedPositionWithColor(viewLocalPoint, color);

      logSender('sticky:zone:create', {
        zoneId,
        cause: 'toolbar_drag',
        viewId: targetView.id,
        viewName: targetView.name.value,
      });
    },
    [clientToCanvasPoint, logSender, models, onSelectView, operations, readonly, selectedView, selectedViewIds, views]
  );

  const handleAddZoneByDragWithColor = useCallback(
    (clientPoint: { x: number; y: number }, color: ThemeColor): void => {
      createZoneByDrag(clientPoint, color);
    },
    [createZoneByDrag]
  );

  return {
    handleAddZone,
    handleAddDescriptionPanel,
    handleAddDescriptionPanelByDrag,
    handleAddComment,
    handleAddCommentByDrag,
    handleAddNodeWithColor,
    handleAddNodeByDragWithColor,
    handleAddZoneWithColor,
    handleAddZoneByDragWithColor,
  };
};
